// Cloudflare Turnstile, rendered explicitly. With implicit rendering every
// .cf-turnstile on the page (newsletter, auth, contact — all in hidden modals)
// started its iframe at load and kept burning CPU in the background (~100 ms/s
// each on mobile), for forms nobody had opened. Instead, the api script loads
// on first use and a widget only exists while its modal is open.
//
// The .cf-turnstile element is only printed when keys are set (turnstile.twig),
// so a form without one skips all of this.

const API_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TOKEN_TIMEOUT = 20000; // ms, then submit anyway and let the server answer

let apiPromise = null;
const widgets = new WeakMap(); // .cf-turnstile element -> { id, token, waiters }

function loadApi() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (apiPromise) return apiPromise;

  apiPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = API_URL;
    script.async = true;
    // With render=explicit the API is usable as soon as the script has run.
    // turnstile.ready() throws on a dynamically injected (async) script.
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => {
      apiPromise = null;
      reject(new Error("[turnstile] failed to load api.js"));
    };
    document.head.appendChild(script);
  });
  return apiPromise;
}

function flush(state) {
  state.waiters.forEach((resolve) => resolve(state.token));
  state.waiters = [];
}

function elementsIn(root) {
  if (!root) return [];
  return root.matches?.(".cf-turnstile") ? [root] : [...root.querySelectorAll(".cf-turnstile")];
}

// Renders the widget(s) inside `root` (a form or a modal) if not already there.
export function mountTurnstile(root) {
  elementsIn(root).forEach((el) => {
    if (widgets.has(el)) return;
    const state = { id: null, token: null, waiters: [] };
    widgets.set(el, state);

    loadApi()
      .then((turnstile) => {
        // Unmounted while the script was loading.
        if (widgets.get(el) !== state) return;
        const { sitekey, appearance, language, size } = el.dataset;
        state.id = turnstile.render(el, {
          sitekey,
          language,
          ...(appearance && { appearance }),
          ...(size && { size }),
          callback: (token) => {
            state.token = token;
            flush(state);
          },
          "expired-callback": () => (state.token = null),
          "error-callback": () => (state.token = null),
        });
      })
      .catch((err) => {
        console.warn(err);
        widgets.delete(el);
        flush(state);
      });
  });
}

// Removes the widget(s) inside `root` so their iframe stops running.
export function unmountTurnstile(root) {
  elementsIn(root).forEach((el) => {
    const state = widgets.get(el);
    if (!state) return;
    widgets.delete(el);
    if (state.id !== null) window.turnstile?.remove(state.id);
    flush(state);
  });
}

// Resolves once the form's widget has a token (mounting it if needed), or after
// TOKEN_TIMEOUT. Resolves right away when the form has no widget.
export function waitForTurnstile(form) {
  const el = elementsIn(form)[0];
  if (!el) return Promise.resolve(null);
  mountTurnstile(form);

  const state = widgets.get(el);
  if (!state) return Promise.resolve(null);
  if (state.token) return Promise.resolve(state.token);

  return new Promise((resolve) => {
    state.waiters.push(resolve);
    setTimeout(() => resolve(null), TOKEN_TIMEOUT);
  });
}

// A Turnstile token is single-use: reset the widget after each submission.
export function resetTurnstile(form) {
  const el = elementsIn(form)[0];
  const state = el && widgets.get(el);
  if (!state || state.id === null) return;
  state.token = null;
  window.turnstile?.reset(state.id);
}
